/**
 * Pure selectors over NavConfig. No React, no MUI, no DOM — config in, data out.
 *
 * The visible navigation for a user is, per group, the pages that are
 * (a) not hidden AND (b) allowed by the user's auth role (minRole) AND
 * (c) present in the catalog. Empty groups are dropped. The tray is never shown
 * in the live navigation.
 */
import { UserRole } from '../services/types';
import { NavConfig, PageId, Perspective } from './navTypes';
import { PAGE_CATALOG } from './pageCatalog';

const ROLE_RANK: Record<UserRole, number> = { viewer: 1, operator: 2, admin: 3 };

export function roleAtLeast(role: UserRole, minRole: UserRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minRole];
}

/** Default perspective seeded from the auth role. Only admins land on Admin. */
export function defaultPerspectiveForRole(role: UserRole): Perspective {
  return role === 'admin' ? 'admin' : 'tutor';
}

function pageAllowed(id: PageId, role: UserRole): boolean {
  const entry = PAGE_CATALOG[id];
  if (!entry) return false; // orphan id no longer in catalog
  return roleAtLeast(role, entry.minRole);
}

export interface VisibleNavItem {
  id: PageId;
  label: string;
  route: string;
}

export interface VisibleNavGroup {
  id: string;
  name: string;
  items: VisibleNavItem[];
}

/** Groups (with their visible items) shown in the sidebar for a perspective+role. */
export function computeVisibleGroups(
  cfg: NavConfig,
  perspective: Perspective,
  role: UserRole
): VisibleNavGroup[] {
  const pn = cfg[perspective];
  if (!pn) return [];
  return pn.groups
    .map((g) => ({
      id: g.id,
      name: g.name,
      items: g.pages
        .filter((p) => !p.hidden && pageAllowed(p.id, role))
        .map((p) => ({
          id: p.id,
          label: PAGE_CATALOG[p.id].label,
          route: PAGE_CATALOG[p.id].route,
        })),
    }))
    .filter((g) => g.items.length > 0);
}

/** Flattened, ordered list of visible page ids for a perspective+role. */
export function visiblePageIds(
  cfg: NavConfig,
  perspective: Perspective,
  role: UserRole
): PageId[] {
  return computeVisibleGroups(cfg, perspective, role).flatMap((g) => g.items.map((i) => i.id));
}

export function firstVisiblePage(
  cfg: NavConfig,
  perspective: Perspective,
  role: UserRole
): PageId | null {
  return visiblePageIds(cfg, perspective, role)[0] ?? null;
}

/**
 * On a perspective switch: if the currently-open path is still visible in the
 * new perspective, stay ({ route: null }); otherwise jump to the first visible
 * page's route (or stay if the new perspective has nothing visible).
 */
export function resolveActivePageOnSwitch(
  cfg: NavConfig,
  perspective: Perspective,
  role: UserRole,
  currentPath: string
): { route: string | null } {
  const items = computeVisibleGroups(cfg, perspective, role).flatMap((g) => g.items);
  if (items.some((i) => i.route === currentPath)) return { route: null };
  const first = items[0];
  return { route: first ? first.route : null };
}
